import { z } from 'zod';
import { candidateProfileResponseSchema, downloadManifestSchema, forgotPasswordResponseSchema, interviewDetailSchema, interviewSchema, invitationSchema, jobProfileSchema, joinTokenResponseSchema, loginResponseSchema, telemetryLogResponseSchema, userSchema, } from '@proctoring/shared';
import { HttpClient } from './http.js';
// The API wraps every payload in a single-key object; these mirror those envelopes so the
// client can unwrap them without any consumer knowing the wire shape.
const meResponseSchema = z.object({ user: userSchema });
const healthResponseSchema = z.object({ status: z.literal('ok'), time: z.iso.datetime() });
const jobProfilesResponseSchema = z.object({ jobProfiles: z.array(jobProfileSchema) });
const jobProfileResponseSchema = z.object({ jobProfile: jobProfileSchema });
const interviewsResponseSchema = z.object({ interviews: z.array(interviewSchema) });
const interviewDetailResponseSchema = z.object({ interview: interviewDetailSchema });
const interviewResponseSchema = z.object({ interview: interviewSchema });
const invitationsResponseSchema = z.object({ invitations: z.array(invitationSchema) });
const invitationResponseSchema = z.object({ invitation: invitationSchema });
const interviewersResponseSchema = z.object({ interviewers: z.array(userSchema) });
export class ApiClient {
    http;
    audience;
    constructor(options, audience = 'portal') {
        if (options instanceof HttpClient) {
            this.http = options;
            this.audience = audience;
        }
        else {
            this.http = new HttpClient(options);
            this.audience = options.audience ?? audience;
        }
    }
    get tokens() {
        return this.http.tokens;
    }
    // --- Health -----------------------------------------------------------------
    /** Unauthenticated reachability probe, used by the candidate system check. */
    health(signal) {
        return this.http.request({
            path: '/health',
            schema: healthResponseSchema,
            anonymous: true,
            ...(signal ? { signal } : {}),
        });
    }
    // --- Downloads (apps/api/src/routes/downloads.ts) ----------------------------
    /**
     * Lists the published desktop installers, newest version first.
     *
     * `anonymous` because the endpoint is public and the candidate portal renders it on a
     * page that may load before a token exists. An empty `versions` array is a valid answer,
     * not an error — callers must render the "nothing published yet" state rather than throw.
     */
    downloadManifest(signal) {
        return this.http.request({
            path: '/downloads/manifest',
            schema: downloadManifestSchema,
            anonymous: true,
            ...(signal ? { signal } : {}),
        });
    }
    /**
     * Absolute URL for an asset, for use as an `<a href>`.
     *
     * The manifest carries origin-relative URLs so the same JSON is correct behind any host;
     * a browser rendering the portal on a different origin than the API needs them joined,
     * and that join belongs here rather than in every component that shows a link.
     */
    downloadUrl(asset) {
        return `${this.http.apiBaseUrl}${asset.url}`;
    }
    // --- Auth (apps/api/src/routes/auth.ts) --------------------------------------
    /**
     * Signs in. The audience is supplied by the client rather than the caller, so a login form
     * cannot accidentally authenticate against the wrong app's rules — and so adding a third
     * front-end is one constructor argument rather than an edit to every call site.
     */
    login(body) {
        return this.http.request({
            method: 'POST',
            path: '/auth/login',
            body: { ...body, audience: body.audience ?? this.audience },
            schema: loginResponseSchema,
            anonymous: true,
        });
    }
    setupPassword(body) {
        return this.http.request({
            method: 'POST',
            path: '/auth/setup-password',
            body,
            schema: loginResponseSchema,
            anonymous: true,
        });
    }
    /**
     * Starts a password recovery. Resolves identically whether or not the address has an
     * account, so callers must not branch on the result — see the route's neutral 202.
     */
    forgotPassword(body) {
        return this.http.request({
            method: 'POST',
            path: '/auth/forgot-password',
            body,
            schema: forgotPasswordResponseSchema,
            anonymous: true,
        });
    }
    /** Redeems a single-use reset token; answers with a session, like `setupPassword`. */
    resetPassword(body) {
        return this.http.request({
            method: 'POST',
            path: '/auth/reset-password',
            body,
            schema: loginResponseSchema,
            anonymous: true,
        });
    }
    async me(signal) {
        const { user } = await this.http.request({
            path: '/auth/me',
            schema: meResponseSchema,
            ...(signal ? { signal } : {}),
        });
        return user;
    }
    // --- Candidate (apps/api/src/routes/candidate.ts) ----------------------------
    candidateProfile(signal) {
        return this.http.request({
            path: '/candidate/me',
            schema: candidateProfileResponseSchema,
            ...(signal ? { signal } : {}),
        });
    }
    candidateJoin(interviewId) {
        return this.http.request({
            method: 'POST',
            path: `/candidate/interviews/${interviewId}/join`,
            schema: joinTokenResponseSchema,
        });
    }
    // --- Interviewer (apps/api/src/routes/interviewer.ts) ------------------------
    async interviewerInterviews(query = {}, signal) {
        const { interviews } = await this.http.request({
            path: '/interviewer/interviews',
            ...(query.state ? { query: { state: query.state } } : {}),
            schema: interviewsResponseSchema,
            ...(signal ? { signal } : {}),
        });
        return interviews;
    }
    async interviewerInterview(interviewId, signal) {
        const { interview } = await this.http.request({
            path: `/interviewer/interviews/${interviewId}`,
            schema: interviewDetailResponseSchema,
            ...(signal ? { signal } : {}),
        });
        return interview;
    }
    interviewerJoin(interviewId) {
        return this.http.request({
            method: 'POST',
            path: `/interviewer/interviews/${interviewId}/join`,
            schema: joinTokenResponseSchema,
        });
    }
    async endInterview(interviewId) {
        const { interview } = await this.http.request({
            method: 'POST',
            path: `/interviewer/interviews/${interviewId}/end`,
            schema: interviewResponseSchema,
        });
        return interview;
    }
    interviewerTelemetry(interviewId, signal) {
        return this.http.request({
            path: `/interviewer/interviews/${interviewId}/telemetry`,
            query: { format: 'json' },
            schema: telemetryLogResponseSchema,
            ...(signal ? { signal } : {}),
        });
    }
    interviewerTelemetryExport(interviewId, format) {
        return this.http.requestBlob({
            path: `/interviewer/interviews/${interviewId}/telemetry`,
            query: { format },
        });
    }
    // --- Admin: job profiles (apps/api/src/routes/admin.ts) ----------------------
    async jobProfiles(signal) {
        const { jobProfiles } = await this.http.request({
            path: '/admin/job-profiles',
            schema: jobProfilesResponseSchema,
            ...(signal ? { signal } : {}),
        });
        return jobProfiles;
    }
    async jobProfile(id, signal) {
        const { jobProfile } = await this.http.request({
            path: `/admin/job-profiles/${id}`,
            schema: jobProfileResponseSchema,
            ...(signal ? { signal } : {}),
        });
        return jobProfile;
    }
    async createJobProfile(body) {
        const { jobProfile } = await this.http.request({
            method: 'POST',
            path: '/admin/job-profiles',
            body,
            schema: jobProfileResponseSchema,
        });
        return jobProfile;
    }
    async updateJobProfile(id, body) {
        const { jobProfile } = await this.http.request({
            method: 'PATCH',
            path: `/admin/job-profiles/${id}`,
            body,
            schema: jobProfileResponseSchema,
        });
        return jobProfile;
    }
    deleteJobProfile(id) {
        return this.http.request({ method: 'DELETE', path: `/admin/job-profiles/${id}` });
    }
    // --- Admin: interviewers ------------------------------------------------------
    async adminInterviewers(signal) {
        const { interviewers } = await this.http.request({
            path: '/admin/interviewers',
            schema: interviewersResponseSchema,
            ...(signal ? { signal } : {}),
        });
        return interviewers;
    }
    async createInterviewer(body) {
        const { user } = await this.http.request({
            method: 'POST',
            path: '/admin/interviewers',
            body,
            schema: meResponseSchema,
        });
        return user;
    }
    // --- Admin: interviews --------------------------------------------------------
    async adminInterviews(query = {}, signal) {
        const { interviews } = await this.http.request({
            path: '/admin/interviews',
            ...(query.state ? { query: { state: query.state } } : {}),
            schema: interviewsResponseSchema,
            ...(signal ? { signal } : {}),
        });
        return interviews;
    }
    async adminInterview(id, signal) {
        const { interview } = await this.http.request({
            path: `/admin/interviews/${id}`,
            schema: interviewDetailResponseSchema,
            ...(signal ? { signal } : {}),
        });
        return interview;
    }
    async createInterview(body) {
        const { interview } = await this.http.request({
            method: 'POST',
            path: '/admin/interviews',
            body,
            schema: interviewResponseSchema,
        });
        return interview;
    }
    /** Moves an interview to a new slot; the API re-emails both sides. */
    async rescheduleInterview(id, body) {
        const { interview } = await this.http.request({
            method: 'PATCH',
            path: `/admin/interviews/${id}`,
            body,
            schema: interviewResponseSchema,
        });
        return interview;
    }
    /**
     * Cancels an interview. The API revokes outstanding invitations, emails both sides and
     * writes an audit row; the reason, when given, is recorded but never sent to the candidate.
     */
    async cancelInterview(id, body = {}) {
        const { interview } = await this.http.request({
            method: 'POST',
            path: `/admin/interviews/${id}/cancel`,
            body,
            schema: interviewResponseSchema,
        });
        return interview;
    }
    async interviewInvitations(interviewId, signal) {
        const { invitations } = await this.http.request({
            path: `/admin/interviews/${interviewId}/invitations`,
            schema: invitationsResponseSchema,
            ...(signal ? { signal } : {}),
        });
        return invitations;
    }
    /** Issues a fresh setup link, invalidating the previous one, and emails it. */
    async resendInvitation(invitationId) {
        const { invitation } = await this.http.request({
            method: 'POST',
            path: `/admin/invitations/${invitationId}/resend`,
            schema: invitationResponseSchema,
        });
        return invitation;
    }
    async revokeInvitation(invitationId) {
        const { invitation } = await this.http.request({
            method: 'POST',
            path: `/admin/invitations/${invitationId}/revoke`,
            schema: invitationResponseSchema,
        });
        return invitation;
    }
    adminTelemetry(interviewId, signal) {
        return this.http.request({
            path: `/admin/interviews/${interviewId}/telemetry`,
            query: { format: 'json' },
            schema: telemetryLogResponseSchema,
            ...(signal ? { signal } : {}),
        });
    }
    adminTelemetryExport(interviewId, format) {
        return this.http.requestBlob({
            path: `/admin/interviews/${interviewId}/telemetry`,
            query: { format },
        });
    }
}
//# sourceMappingURL=client.js.map